<template>
    <div
        class="flex w-full flex-col rounded-lg p-4 shadow-sm ring-1 ring-iron-300"
    >
        <div class="items-start justify-between sm:flex">
            <div v-if="!subscription">
                <h2 class="text-xl font-medium text-iron-900">
                    {{ pricingData.title }}
                </h2>

                <p class="mt-4">
                    <span class="text-2xl font-bold text-iron-900">
                        {{ pricingData.currency_price_yearly }}
                    </span>

                    <span class="text-lg font-semibold text-iron-800"
                        >/{{ $t("billing.card.monthly") }}
                    </span>
                    <span
                        >&nbsp; {{ $t("billing.card.billed_annually") }}
                    </span>
                </p>
                <p class="mt-1">
                    <span>
                        {{ pricingData.currency_price_monthly }}
                    </span>
                    <span class="text-sm"
                        >/{{ $t("billing.card.monthly") }}
                    </span>
                    <span class="text-sm"
                        >&nbsp;{{ $t("billing.card.month_to_month") }}
                    </span>
                </p>
            </div>

            <div v-if="subscription && activePlan">
                <div class="mb-2 flex gap-1.5">
                    <Badge :variant="'outline'">{{
                        $t("billing.card.active")
                    }}</Badge>
                </div>
                <div>
                    <h2 class="text-xl font-medium text-iron-900">
                        {{ activePlan?.name }}
                    </h2>

                    <p class="mt-2 font-medium">
                        {{
                            formatPriceCurrency(
                                activePlan.price,
                                activePlan.currency,
                            )
                        }}
                        /
                        {{
                            activePlan.recurring === "monthly"
                                ? $t("billing.card.monthly")
                                : $t("billing.card.yearly")
                        }}
                    </p>
                </div>
            </div>

            <div
                v-if="subscription && activePlan"
                class="flex h-full flex-col justify-between"
            >
                <div
                    v-if="!subscription.ends_at"
                    class="h-full items-end gap-2 sm:flex"
                >
                    <Button
                        size="sm"
                        variant="outline"
                        @click="emit('switchPlan')"
                        >{{
                            activePlan.recurring === "monthly"
                                ? $t("billing.card.switch_to_yearly")
                                : $t("billing.card.switch_to_monthly")
                        }}</Button
                    >

                    <Button
                        size="sm"
                        class="ml-3 mt-4 sm:ml-0 sm:mt-0"
                        variant="outline"
                        @click="emit('cancelSubscription')"
                        >{{ $t("billing.card.cancel") }}</Button
                    >
                </div>

                <div
                    v-if="subscription.ends_at"
                    class="flex flex-col justify-between sm:h-full"
                >
                    <p class="text-sm text-iron-800">
                        {{ $t("billing.card.active_until") }}
                        <span class="font-medium">{{
                            format(new Date(subscription.ends_at), "PPP")
                        }}</span>
                    </p>

                    <Button
                        class="ml-3 mt-4 sm:ml-0 sm:mt-0"
                        @click="emit('resume')"
                        >{{ $t("billing.card.resume") }}</Button
                    >
                </div>
            </div>

            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { PricingData } from "../composables/usePricingData";
import type { Plan, Subscription } from "../types";
import { Button } from "@/components/ui/button";
import { formatPriceCurrency } from "../utils/formatPriceCurrency";
import { Badge } from "@/components/ui/badge";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";

defineProps<{
    pricingData: PricingData;
    subscription?: Subscription;
    activePlan?: Plan;
}>();

const { format } = useLocalisedTzFormat();

const emit = defineEmits(["switchPlan", "cancelSubscription", "resume"]);
</script>
